/**
 * OKS-931 — Currency formatting for the catalogue.
 *
 * ⚠️ No i18n key: there are no WORDS in a formatted price, only a locale and
 * a currency code, and `Intl.NumberFormat` owns both. A key here would be a
 * translatable string with nothing translatable in it (UX §3.4).
 *
 * Prices are stored in the store's DECLARED currency (OKS-930 D2). A store
 * whose declared currency is wrong shows every price mislabelled and nothing
 * in the payload can contradict it — an accepted limitation (PRD §5).
 */
export function formatPrice(
  cents: number | null,
  currency: string,
  language: string,
): string | null {
  // A product we could not price has NO price, not a zero one. Rendering
  // "0 €" for an unmapped price would advertise a free gift.
  if (cents === null) return null

  try {
    return new Intl.NumberFormat(language === "en" ? "en-US" : "fr-FR", {
      style: "currency",
      currency,
    }).format(cents / 100)
  } catch {
    // A currency code Intl rejects (a tampered or legacy row) must not throw
    // the whole list away. Degrade to the number plus the raw code.
    return `${(cents / 100).toFixed(2)} ${currency}`
  }
}

/**
 * The list shows a RANGE when a product's variants disagree on price, and a
 * single value when they do not. The en dash is a JSX element at the call
 * site, not a character inside a translated string.
 */
export function formatPriceRange(
  minCents: number | null,
  maxCents: number | null,
  currency: string,
  language: string,
): { min: string; max: string | null } | null {
  const min = formatPrice(minCents, currency, language)
  if (min === null) return null

  const max = formatPrice(maxCents, currency, language)

  return { min, max: max !== null && max !== min ? max : null }
}
